import { useState, useCallback, useEffect, type ReactNode } from 'react';
import type { Product, ProductContextValue } from '@/types';
import { products, getProductById } from '@/data/products';
import { ProductContext } from './productContext';

export interface ProductProviderProps {
  children: ReactNode;
}

// SessionStorage key for persisting selected product during auth flows
const SELECTED_PRODUCT_KEY = 'selected_product_id';

/**
 * Load selected product ID from sessionStorage
 * Returns null if no saved ID or if ID is invalid
 */
function loadProductIdFromSession(): string | null {
  try {
    const savedId = sessionStorage.getItem(SELECTED_PRODUCT_KEY);
    return savedId || null;
  } catch (error) {
    console.warn('[ProductProvider] Failed to load product ID from session storage:', error);
    return null;
  }
}

/**
 * Save selected product ID to sessionStorage
 */
function saveProductIdToSession(productId: string | null): void {
  try {
    if (productId) {
      sessionStorage.setItem(SELECTED_PRODUCT_KEY, productId);
    } else {
      sessionStorage.removeItem(SELECTED_PRODUCT_KEY);
    }
  } catch (error) {
    console.warn('[ProductProvider] Failed to save product ID to session storage:', error);
  }
}

export function ProductProvider({ children }: ProductProviderProps) {
  // Load from sessionStorage on mount to preserve selection during auth flows
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(() => {
    const savedId = loadProductIdFromSession();
    if (!savedId) return null;
    const product = getProductById(savedId);
    return product || null;
  });

  // Auto-save to sessionStorage whenever selected product changes
  useEffect(() => {
    saveProductIdToSession(selectedProduct?.id || null);
  }, [selectedProduct]);

  const selectProduct = useCallback((productId: string) => {
    const product = getProductById(productId);
    if (product) {
      setSelectedProduct(product);
    }
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedProduct(null);
  }, []);

  const value: ProductContextValue = {
    selectedProduct,
    products,
    selectProduct,
    clearSelection,
  };

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
}
